
import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Double Fault Club PDP gallery.
 * Shopify Media order: 1 White mockup, 2 human studio photo,
 * 3 close-up (optional), 4+ colour mockups.
 * Catalogue featured image remains image 1.
 * Works for the PDP CAROUSEL; desktop GRID ordering is controlled in Liquid.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #variantUpdated = false;
  #lastVariantMediaId = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    this.#variantUpdated = false;
    this.#lastVariantMediaId = null;

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    this.#ensureSlideMediaIds();
    this.#selectOpeningHumanPhoto();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  // Some Horizon versions do not output slide-id attributes.
  #ensureSlideMediaIds() {
    if (!this.slideshow) return;

    this.slideshow.querySelectorAll('slideshow-slide').forEach((slide) => {
      if (slide.hasAttribute('slide-id')) return;
      const mediaId = slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId;
      if (mediaId) slide.setAttribute('slide-id', mediaId);
    });
  }

  // Open with image 2 (human) without changing Shopify's featured image.
  #selectOpeningHumanPhoto() {
    if (!window.location.pathname.includes('/products/')) return;
    if (this.closest('dialog')) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    if (slides.length < 2 || !slides[1].querySelector('.product-media__image')) return;

    // Remember the mockup belonging to the initial White variant.
    this.#lastVariantMediaId = slides[0].getAttribute('slide-id');

    // Horizon reads initial-slide during slideshow initialization.
    slideshow.setAttribute('initial-slide', '1');
    requestAnimationFrame(() => {
      if (!this.isConnected || this.#variantUpdated || this.slideshow !== slideshow) return;
      slideshow.select(1, undefined, { animate: false });
    });
  }

  /** @param {VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const featuredMediaId = event.detail?.resource?.featured_media?.id;
    if (!featuredMediaId || !this.slideshow) return;

    const nextMediaId = String(featuredMediaId);

    // Do not change images when only Size changes (same variant image).
    if (nextMediaId === this.#lastVariantMediaId) return;

    this.#variantUpdated = true;
    this.#lastVariantMediaId = nextMediaId;
    this.#ensureSlideMediaIds();

    this.slideshow.select(
      { id: nextMediaId },
      undefined,
      { animate: false }
    );
  };

  /** @param {ZoomMediaSelectedEvent} event */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /** @param {number} index @param {PointerEvent} event */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
